/* eslint-disable sonarjs/no-duplicate-string */
import React from 'react';
import { useParams } from 'react-router-dom';

import envs from 'envs';
import PropTypes from 'prop-types';

import { usePermissions, useSeller } from 'hooks';

// Components
import { Mui } from 'components';
import { NavBar as DefaultNavBar } from 'components/layouts';

export const useStyles = Mui.makeStyles(theme => ({
  chipAlert: {
    color: theme.palette?.error?.contrastText,
    backgroundColor: theme.palette?.error?.main,
  },
}));

function NavBar({ onMobileClose, openMobile, sellerPermissions, schoolManagement, ...restProps }) {
  // Hooks
  const { sellerSlug } = useParams();
  const { permissions } = usePermissions();
  const { seller } = useSeller();
  const classes = useStyles();

  // Constants
  const { isStaff } = permissions || {};
  const { logo: image, cover, displayName, pendingTasks } = seller || {};
  const isSellerInstitution = !!seller && !!seller.institution;

  // Props
  const profile = { image, cover, displayName, profileUrl: '/sellers' };

  const sellerPath = `/sellers/${sellerSlug}`;
  const items = [
    {
      subheader: 'Minha conta',
      items: [
        {
          title: 'Início',
          href: sellerPath,
          icon: newProps => <Mui.Icon {...newProps}>home_outlined</Mui.Icon>,
        },
        {
          title: 'Perfil',
          href: [sellerPath, 'profile'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>business</Mui.Icon>,
          invisible: !sellerPermissions?.includes('account.profile'),
        },
        {
          title: 'Cobrança',
          icon: newProps => <Mui.Icon {...newProps}>receipt_long</Mui.Icon>,
          items: [
            { title: 'Planos', href: [sellerPath, 'billing', 'plan'].join('/') },
            { title: 'Faturas', href: [sellerPath, 'billing', 'invoices'].join('/') },
            {
              title: 'Notas fiscais de faturas e resgates',
              href: [sellerPath, 'billing', 'tax-invoices'].join('/'),
            },
            {
              title: 'Cartões de crédito',
              href: [sellerPath, 'billing', 'credit-cards'].join('/'),
            },
            {
              title: 'Contas bancárias e Pix',
              href: [sellerPath, 'billing', 'bank-accounts'].join('/'),
              invisible: !sellerPermissions?.includes('account.bank_accounts'),
            },
          ].filter(item => !item.invisible),
          invisible:
            !sellerPermissions?.includes('billingmanager.billingmanager') ||
            sellerSlug === envs.APPLICATION_SLUG,
        },
        {
          title: 'Notificações',
          href: [sellerPath, 'notifications'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>notifications</Mui.Icon>,
          invisible: !sellerPermissions?.includes('account.notifications'),
        },
        {
          title: 'Políticas',
          href: [sellerPath, 'policies'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>gavel</Mui.Icon>,
          invisible: !sellerPermissions?.includes('account.policies'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Programa de pontos',
      items: [
        {
          title: 'Meus pontos',
          href: [sellerPath, 'points'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>stars</Mui.Icon>,
        },
        {
          title: 'Minhas indicações',
          href: [sellerPath, 'points', 'partner-referral'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>person_add</Mui.Icon>,
        },
        {
          title: 'Troque seus pontos',
          href: [sellerPath, 'points', 'catalog-items'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>emoji_events</Mui.Icon>,
        },
        {
          title: 'Como acumular pontos',
          href: [sellerPath, 'points', 'rules'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>live_help</Mui.Icon>,
        },
        {
          title: 'Regulamento',
          href: [sellerPath, 'points', 'regulations'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>gavel</Mui.Icon>,
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Eskolare Insights',
      items: [
        {
          title: 'Indicadores',
          icon: newProps => <Mui.Icon {...newProps}>dashboard</Mui.Icon>,
          items: [
            { title: 'Geral', href: [sellerPath, 'indicators', 'general'].join('/') },
            { title: 'Pagamentos', href: [sellerPath, 'indicators', 'payments'].join('/') },
            { title: 'Financeiro', href: [sellerPath, 'indicators', 'financial'].join('/') },
            { title: 'Eskolare Desk', href: [sellerPath, 'indicators', 'eskolare-desk'].join('/') },
            {
              title: 'Acordos de vendas',
              href: [sellerPath, 'indicators', 'sales-agreement'].join('/'),
            },
          ],
        },
        {
          title: 'Carrinhos abandonados',
          href: [sellerPath, 'sales', 'abandoned-carts'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>remove_shopping_cart</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.baskets'),
        },
        {
          title: 'Avaliações de usuários',
          href: [sellerPath, 'nps-score'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>reviews</Mui.Icon>,
        },
        {
          title: 'Configurações',
          icon: newProps => <Mui.Icon {...newProps}>settings</Mui.Icon>,
          items: [{ title: 'Tags', href: [sellerPath, 'tags'].join('/') }],
        },
      ].filter(item => !item.invisible),
      invisible: !sellerPermissions?.includes('analytics.dashboards'),
    },
    {
      subheader: 'Pessoas e acesso',
      items: [
        {
          title: 'Grupos',
          href: [sellerPath, 'groups'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>supervised_user_circle_icon</Mui.Icon>,
          invisible: !sellerPermissions?.includes('permissions.groups'),
        },
        {
          title: 'Usuários',
          href: [sellerPath, 'users'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>account_circle</Mui.Icon>,
          invisible: !sellerPermissions?.includes('permissions.users'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Suporte da plataforma',
      items: [
        {
          title: 'Atendimentos',
          href: [sellerPath, 'support', 'platform-tickets'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>question_answer</Mui.Icon>,
          invisible: sellerSlug === envs.APPLICATION_SLUG,
        },
        {
          title: 'Artigos de ajuda',
          href: [sellerPath, 'support', 'platform-articles'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>help</Mui.Icon>,
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Suporte aos clientes',
      items: [
        {
          title: 'Chamados',
          href: [sellerPath, 'support', 'tickets'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>support_agent</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('support.can_manage_tickets') &&
            !sellerPermissions?.includes('support.can_view_tickets'),
        },
        {
          title: 'Indicadores',
          href: [sellerPath, 'support', 'indicators'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>show_chart</Mui.Icon>,
        },
        {
          title: 'Configurações do suporte',
          href: [sellerPath, 'support', 'configuration'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>settings_applications_icon</Mui.Icon>,
          invisible: !sellerPermissions?.includes('support.configurations'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Gestão',
      items: [
        {
          title: 'Pendências',
          href: [sellerPath, 'tasks'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>railway_alert</Mui.Icon>,
          info: () =>
            pendingTasks ? (
              <Mui.Chip size="small" label={pendingTasks} className={classes.chipAlert} />
            ) : null,
          badgeContent: pendingTasks,
          invisible: !sellerPermissions?.includes('tasks.tasks'),
        },
        {
          title: 'Vendas',
          href: [sellerPath, 'sales', 'orders'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>check_box_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('orders.orders'),
          items: [
            {
              title: 'Pedidos',
              href: [sellerPath, 'sales', 'orders'].join('/'),
            },
            {
              title: 'Indicadores',
              href: [sellerPath, 'sales', 'indicators'].join('/'),
            },
            {
              title: 'Carrinhos abandonados',
              href: [sellerPath, 'sales', 'abandoned-carts'].join('/'),
              invisible: !sellerPermissions?.includes('shops.baskets'),
            },
          ].filter(item => !item.invisible),
        },
        {
          title: 'Notas fiscais',
          href: [sellerPath, 'tax-invoices', 'my-invoices'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>receipt_long</Mui.Icon>,
          invisible: !sellerPermissions?.includes('orders.orders'),
          items: [
            {
              title: 'Minhas notas fiscais',
              href: [sellerPath, 'tax-invoices', 'my-invoices'].join('/'),
            },
            {
              title: 'Aprovações de comissionamento',
              href: [sellerPath, 'tax-invoices', 'commission-approvals'].join('/'),
              invisible: !sellerPermissions?.includes('financial.withdrawals'),
            },
          ].filter(item => !item.invisible),
        },
        {
          title: 'Entrega na instituição',
          href: [sellerPath, 'institution-shipment'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>home_work</Mui.Icon>,
          invisible: !sellerPermissions?.includes('logistics.institution_shipments'),
        },
        {
          title: 'Acordos de vendas',
          href: [sellerPath, 'sales-agreements', 'registration'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>draw</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('sale_agreement.can_view_sale_agreement') &&
            !sellerPermissions?.includes('sale_agreement.can_manage_sale_agreement'),
          items: [
            {
              title: 'Gestão de acordo',
              href: [sellerPath, 'sales-agreements', 'registration'].join('/'),
            },
            {
              title: 'Indicadores',
              href: [sellerPath, 'sales-agreements', 'indicators'].join('/'),
            },
            {
              title: 'Contas de comissão',
              href: [sellerPath, 'sales-agreements', 'commission-accounts'].join('/'),
              invisible:
                !sellerPermissions?.includes('financial.can_manage_commission_payable') &&
                !sellerPermissions?.includes('financial.can_manage_commission_receivable'),
            },
          ].filter(item => !item.invisible),
        },
        {
          title: 'Portal de vendas B2B',
          href: [sellerPath, 'b2b-sales-portal'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>business_center</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('sale_agreement.can_view_sale_agreement') &&
            !sellerPermissions?.includes('sale_agreement.can_manage_sale_agreement'),
          items: [
            { title: 'Acordos de vendas B2B', href: [sellerPath, 'b2b-sales-portal'].join('/') },
            {
              title: 'Indicadores',
              href: [sellerPath, 'b2b-sales-portal', 'performance'].join('/'),
            },
          ],
        },
        {
          title: 'Financeiro',
          href: [sellerPath, 'financial'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>attach_money_outlined</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('financial.commissions') &&
            !sellerPermissions?.includes('financial.statement') &&
            !sellerPermissions?.includes('financial.can_manage_commission_payable') &&
            !sellerPermissions?.includes('financial.can_manage_commission_receivable'),
          items: [
            {
              title: 'Conta-corrente',
              href: [sellerPath, 'financial'].join('/'),
              invisible: !sellerPermissions?.includes('financial.statement'),
            },
            {
              title: 'Indicadores',
              href: [sellerPath, 'financial', 'indicators'].join('/'),
            },
            {
              title: 'Comissionamentos',
              href: [sellerPath, 'financial/arrangements'].join('/'),
              invisible: !sellerPermissions?.includes('financial.commissions'),
            },
            {
              title: 'Informe de saldo e comissões',
              href: [sellerPath, `financial/income-report/${new Date().getFullYear() - 1}`].join(
                '/',
              ),
              invisible: !sellerPermissions?.includes('financial.statement'),
            },
          ].filter(item => !item.invisible),
        },
        {
          title: 'Pagamentos',
          href: [sellerPath, 'payments', 'overview'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>payment</Mui.Icon>,
          invisible: !sellerPermissions?.includes('payments.payments'),
          items: [
            {
              title: 'Visão geral',
              href: [sellerPath, 'payments', 'overview'].join('/'),
            },
            {
              title: 'Indicadores',
              href: [sellerPath, 'payments', 'indicators'].join('/'),
            },
            {
              title: 'Reembolsos',
              href: [sellerPath, 'payments', 'refunds'].join('/'),
            },
            {
              title: 'Contestações (chargebacks)',
              href: [sellerPath, 'payments', 'chargebacks'].join('/'),
              invisible: !sellerPermissions?.includes('payments.chargebacks'),
              isNew: true,
            },
          ].filter(item => !item.invisible),
        },
        {
          title: 'Tenho interesse',
          href: [sellerPath, 'product-alerts'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>notifications_active</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.product_alerts'),
        },
        {
          title: 'Alertas de estoque',
          href: [sellerPath, 'stock-alerts'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>warning</Mui.Icon>,
          invisible: !sellerPermissions?.includes('logistics.stock_alerts'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Mensalidades e matrículas',
      items: [
        {
          title: 'Configuração inicial',
          href: [sellerPath, 'school-management', 'initial-setup'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>ballot</Mui.Icon>,
          invisible: isSellerInstitution || !isStaff,
          isStaff: true,
        },
        {
          title: 'Alunos',
          href: [sellerPath, 'school-management', 'institution-students'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>face</Mui.Icon>,
          isStaff: true,
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_students') &&
              !sellerPermissions?.includes('school_management.can_view_students')) ||
            !isSellerInstitution ||
            !isStaff,
        },
        {
          title: 'Matrículas',
          href: [sellerPath, 'school-management', 'enrollments'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>recent_actors</Mui.Icon>,
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_students') &&
              !sellerPermissions?.includes('school_management.can_view_students')) ||
            !isSellerInstitution,
        },
        {
          title: 'Cobranças',
          href: [sellerPath, 'school-management', 'contract-payments'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>local_atm</Mui.Icon>,
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_payments') &&
              !sellerPermissions?.includes('school_management.can_view_payments')) ||
            !isSellerInstitution,
        },
        {
          title: 'Negociações',
          href: [sellerPath, 'school-management', 'institution-contracts'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>work</Mui.Icon>,
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_contracts') &&
              !sellerPermissions?.includes('school_management.can_view_contracts')) ||
            !isSellerInstitution,
        },
        {
          title: 'Negociações agrupadas',
          href: [sellerPath, 'school-management', 'institution-contract-groups'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>post_add</Mui.Icon>,
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_contracts') &&
              !sellerPermissions?.includes('school_management.can_view_contracts')) ||
            !isSellerInstitution,
        },
        {
          title: 'Régua de cobrança',
          href: [sellerPath, 'school-management', 'billing-rule'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>straighten</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('school_management.can_manage_configurations') ||
            !isSellerInstitution,
        },
        {
          title: 'Indicadores',
          href: [sellerPath, 'school-management', 'performance'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>show_chart</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('school_management.can_view_indicators') ||
            !isSellerInstitution,
        },
        {
          title: 'Loja',
          icon: newProps => <Mui.Icon {...newProps}>storefront</Mui.Icon>,
          items: [
            {
              title: 'Visão geral',
              href: [sellerPath, 'school-management', 'tuition-store', 'insights'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>insights</Mui.Icon>,
            },
            {
              title: 'Vitrines',
              href: [sellerPath, 'school-management', 'tuition-store', 'showcases'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>account_tree</Mui.Icon>,
            },
            {
              title: 'Configurações',
              href: [sellerPath, 'school-management', 'tuition-store', 'configurations'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>settings</Mui.Icon>,
            },
          ].filter(item => !item.invisible),
          isNew: true,
          isStaff: true,
          invisible:
            !schoolManagement?.configuration?.enrollmentStoreEnabled ||
            !isSellerInstitution ||
            (!sellerPermissions?.includes('school_management.can_manage_enrollment_store') &&
              !sellerPermissions?.includes('school_management.can_view_enrollment_store')),
        },
        {
          title: 'Cursos',
          icon: newProps => <Mui.Icon {...newProps}>school</Mui.Icon>,
          items: [
            {
              title: 'Visão geral',
              href: [sellerPath, 'school-management', 'configurations', 'courses', 'insights'].join(
                '/',
              ),
              icon: newProps => <Mui.Icon {...newProps}>insights</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('school_management.can_manage_configurations') &&
                !sellerPermissions?.includes('school_management.can_view_configurations'),
            },
            {
              title: 'Cursos',
              href: [sellerPath, 'school-management', 'configurations', 'courses'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>school</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('school_management.can_manage_configurations') &&
                !sellerPermissions?.includes('school_management.can_view_configurations'),
            },
            {
              title: 'Turmas',
              href: [sellerPath, 'school-management', 'configurations', 'class-groups'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>groups</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('school_management.can_manage_configurations') &&
                !sellerPermissions?.includes('school_management.can_view_configurations'),
            },
          ].filter(item => !item.invisible),
          invisible:
            !sellerPermissions?.includes('school_management.can_manage_configurations') &&
            !sellerPermissions?.includes('school_management.can_view_configurations'),
          isNew: true,
        },
        {
          title: 'Configurações',
          icon: newProps => <Mui.Icon {...newProps}>settings_applications_icon</Mui.Icon>,
          items: [
            {
              title: 'Geral',
              href: [sellerPath, 'school-management', 'configurations'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>tune</Mui.Icon>,
              invisible: !sellerPermissions?.includes(
                'school_management.can_manage_configurations',
              ),
            },
            {
              title: 'Modelos de matrícula',
              href: [
                sellerPath,
                'school-management',
                'configurations',
                'institution-enrollment-templates',
              ].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>recent_actors_outline</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('school_management.can_manage_configurations') &&
                !sellerPermissions?.includes('school_management.can_view_configurations'),
            },
            {
              title: 'Modelos de negociação',
              href: [
                sellerPath,
                'school-management',
                'configurations',
                'institution-contract-templates',
              ].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>work_outline</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('school_management.can_manage_contracts') &&
                !sellerPermissions?.includes('school_management.can_view_contracts'),
            },
            {
              title: 'Formulários',
              href: [sellerPath, 'forms'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>article</Mui.Icon>,
              invisible:
                !sellerPermissions?.includes('forms.can_manage_forms') &&
                !sellerPermissions?.includes('forms.can_view_forms'),
            },
            {
              title: 'Modelos de documentos',
              href: [sellerPath, 'sign', 'envelope-templates'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>document_scanner</Mui.Icon>,
              invisible: !sellerPermissions?.includes('sign.sign'),
            },
          ].filter(item => !item.invisible),
          invisible:
            (!sellerPermissions?.includes('school_management.can_manage_configurations') &&
              !sellerPermissions?.includes('school_management.can_view_configurations')) ||
            !isSellerInstitution,
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Relacionamento',
      items: [
        {
          title: 'Clientes',
          href: [sellerPath, 'customers'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>contact_mail</Mui.Icon>,
          invisible: !sellerPermissions?.includes('cx.customers'),
        },
        {
          title: 'Parcerias',
          href: [sellerPath, 'partnerships'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>diversity_1</Mui.Icon>,
          invisible: !sellerPermissions?.includes('partnerships.partnerships'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Loja',
      items: [
        {
          title: 'Sites',
          href: [sellerPath, 'sites'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>web</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.sites'),
        },
        {
          title: 'Canais',
          href: [sellerPath, 'channels'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>store_front</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.channels'),
        },
        {
          title: 'Solicitações de acesso',
          href: [sellerPath, 'channels-access-request'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>co_present</Mui.Icon>,
          invisible: !sellerPermissions?.includes('cx.customers'),
        },
        {
          title: 'Concierge',
          href: [sellerPath, 'concierge'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>add_shopping_cart_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('cx.concierge'),
        },
        {
          title: 'Carrinhos',
          href: [sellerPath, 'baskets'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>shopping_basket</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.baskets'),
        },
        {
          title: 'Links de pagamento',
          href: [sellerPath, 'pay-by-links'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>link</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.payment_links'),
        },
        {
          title: 'Alocações',
          href: [sellerPath, 'items'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>inventory</Mui.Icon>,
          invisible: !sellerPermissions?.includes('shops.allocations'),
        },
        {
          title: 'Avaliações de usuários',
          href: [sellerPath, 'user-reviews'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>reviews</Mui.Icon>,
        },
        {
          title: 'Destaques',
          icon: newProps => <Mui.Icon {...newProps}>stars</Mui.Icon>,
          items: [
            {
              title: 'Destaques',
              href: [sellerPath, 'featured'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>star</Mui.Icon>,
            },
            {
              title: 'Modelos de destaques',
              href: [sellerPath, 'featured-template'].join('/'),
              icon: newProps => <Mui.Icon {...newProps}>star_border</Mui.Icon>,
            },
          ].filter(item => !item.invisible),
          invisible: !sellerPermissions?.includes('shops.featured'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Promoções',
      items: [
        {
          title: 'Listas',
          href: [sellerPath, 'ranges'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>list_alt</Mui.Icon>,
          invisible: !sellerPermissions?.includes('promotions.ranges'),
        },
        {
          title: 'Ofertas',
          href: [sellerPath, 'offers'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>redeem</Mui.Icon>,
          invisible: !sellerPermissions?.includes('promotions.offers'),
        },
        {
          title: 'Cupons',
          href: [sellerPath, 'vouchers'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>loyalty_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('promotions.vouchers'),
        },
        {
          title: 'Grupos de cupons',
          href: [sellerPath, 'voucher-sets'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>splitscreen</Mui.Icon>,
          invisible: !sellerPermissions?.includes('promotions.vouchers'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Configurações',
      items: [
        {
          title: 'Modelos de mensalidades',
          href: [sellerPath, 'tuition'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>event</Mui.Icon>,
          invisible: !sellerPermissions?.includes('pricing.tuition_templates'),
        },
        {
          title: 'Modelos de contratos',
          href: [sellerPath, 'contract'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>date_range</Mui.Icon>,
          invisible: !sellerPermissions?.includes('pricing.contract_templates'),
        },
        {
          title: 'Assinaturas',
          href: [sellerPath, 'subscriptions'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>subscriptions_outlined</Mui.Icon>,
          disabled: true,
          invisible: true,
        },
        {
          title: 'Logística',
          href: [sellerPath, 'logistics'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>local_shipping</Mui.Icon>,
          invisible: !sellerPermissions?.includes('logistics.warehouses'),
        },
        {
          title: 'Grupos de faturamento',
          href: [sellerPath, 'billing-groups'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>price_change</Mui.Icon>,
          invisible: !sellerPermissions?.includes('orders.orders'),
        },
        {
          title: 'Tags',
          href: [sellerPath, 'settings', 'tags'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>local_offer</Mui.Icon>,
          invisible: !sellerPermissions?.includes('analytics.dashboards'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Catálogo',
      items: [
        {
          title: 'Produtos e serviços',
          href: [sellerPath, 'products'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>shopping_basket</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.products'),
        },
        {
          title: 'Produtos e serviços',
          menuKey: 'Produtos e serviços v2',
          href: [sellerPath, 'products-and-services'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>inventory_2</Mui.Icon>,
          isStaff: true,
          isNew: true,
          invisible: !isStaff || !sellerPermissions?.includes('catalog.products'),
        },
        {
          title: 'Complementos',
          href: [sellerPath, 'options'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>ballot</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.products'),
        },
        {
          title: 'Sugestões de produtos e serviços',
          href: [sellerPath, 'suggestions'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>import_contacts</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.suggestions'),
        },
        {
          title: 'Preços',
          href: [sellerPath, 'prices'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>money_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('pricing.prices'),
        },
        {
          title: 'Marcas',
          href: [sellerPath, 'brands'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>label_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.brands'),
        },
        {
          title: 'Classes de produtos',
          href: [sellerPath, 'product-classes'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>request_quote</Mui.Icon>,
          invisible: !sellerPermissions?.includes('catalog.product_classes'),
        },
      ].filter(item => !item.invisible),
    },
    {
      subheader: 'Outros',
      items: [
        {
          title: 'Formulários',
          href: [sellerPath, 'forms'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>article</Mui.Icon>,
          invisible:
            !sellerPermissions?.includes('forms.can_manage_forms') &&
            !sellerPermissions?.includes('forms.can_view_forms'),
        },
        {
          title: 'Assinatura digital',
          icon: newProps => <Mui.Icon {...newProps}>document_scanner</Mui.Icon>,
          items: [
            {
              title: 'Documentos',
              href: [sellerPath, 'sign', 'envelopes'].join('/'),
              invisible: !sellerPermissions?.includes('sign.sign'),
            },
            {
              title: 'Modelos de documentos',
              href: [sellerPath, 'sign', 'envelope-templates'].join('/'),
              invisible: !sellerPermissions?.includes('sign.sign'),
            },
          ].filter(item => !item.invisible),
          invisible: !sellerPermissions?.includes('sign.sign'),
        },
        {
          title: 'Integrações',
          href: [sellerPath, 'integrations'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>extension_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('integrations.integrations'),
        },
        {
          title: 'Power-ups',
          href: [sellerPath, 'power-ups'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>flash_on_outlined</Mui.Icon>,
          invisible: !sellerPermissions?.includes('integrations.powerups'),
        },
        {
          title: 'Dados',
          href: [sellerPath, 'jobs'].join('/'),
          icon: newProps => <Mui.Icon {...newProps}>cached_outlined</Mui.Icon>,
          invisible: true,
        },
      ].filter(item => !item.invisible),
      invisible:
        !sellerPermissions?.includes('forms.can_manage_forms') &&
        !sellerPermissions?.includes('forms.can_view_forms') &&
        !sellerPermissions?.includes('sign.sign') &&
        !sellerPermissions?.includes('integrations.integrations') &&
        !sellerPermissions?.includes('integrations.powerups'),
    },
  ].filter(item => !item.invisible);

  return (
    <DefaultNavBar
      onMobileClose={onMobileClose}
      openMobile={openMobile}
      items={items}
      profile={profile}
      {...restProps}
    />
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  sellerPermissions: PropTypes.array,
  schoolManagement: PropTypes.object,
};

export default NavBar;
